import React from 'react'
import { createRoot } from 'react-dom/client'
import { PatientInstructionsPrintView } from '../components/PatientInstructionsPrintView'
import { Patient } from '../types/patient'
import { PracticeLocation } from '../types/practiceLocation'
import '../components/PatientInstructionsPrintView.css'

export interface PrintPatientInstructionsParams {
  patient: Patient
  patientInstructions: string
  instructionsGiven?: boolean
  instructionsGivenAt?: string
  instructionsGivenByName?: string
  instructionsGivenBy?: string
  practiceLocation?: PracticeLocation
  logoUrl?: string | null
}

/**
 * Print patient instructions using React component in a new window
 */
export function printPatientInstructions(params: PrintPatientInstructionsParams): void {
  const {
    patient,
    patientInstructions,
    instructionsGiven,
    instructionsGivenAt,
    instructionsGivenByName,
    instructionsGivenBy,
    practiceLocation,
    logoUrl,
  } = params

  const printWindow = window.open('', '_blank', 'width=800,height=600')
  if (!printWindow) {
    alert('Please allow popups to print patient instructions')
    return
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Patient Instructions - ${patient.firstName} ${patient.lastName}</title>
        <meta charset="utf-8">
        <style>
          body {
            margin: 0;
            padding: 0;
          }
        </style>
      </head>
      <body>
        <div id="patient-instructions-root"></div>
      </body>
    </html>
  `)
  printWindow.document.close()

  const checkReady = setInterval(() => {
    if (printWindow.document.readyState === 'complete') {
      clearInterval(checkReady)

      const styleElement = printWindow.document.createElement('style')
      styleElement.textContent = getPrintStylesCSS()
      printWindow.document.head.appendChild(styleElement)

      const rootElement = printWindow.document.getElementById('patient-instructions-root')
      if (rootElement) {
        const root = createRoot(rootElement)
        root.render(
          React.createElement(PatientInstructionsPrintView, {
            patient,
            patientInstructions,
            instructionsGiven,
            instructionsGivenAt,
            instructionsGivenByName,
            instructionsGivenBy,
            practiceLocation,
            logoUrl,
          }),
        )

        setTimeout(() => {
          printWindow.print()
        }, 500)
      }
    }
  }, 50)
}

function getPrintStylesCSS(): string {
  return `
    .patient-instructions-print-view {
      max-width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 20mm;
      background: white;
      font-family: 'Times New Roman', serif;
      font-size: 11pt;
      line-height: 1.4;
      color: #000;
    }

    .pi-print-header {
      display: flex;
      align-items: center;
      gap: 24px;
      margin-bottom: 20px;
      padding-bottom: 15px;
      border-bottom: 1px solid #ddd;
    }

    .pi-logo-container {
      flex-shrink: 0;
    }

    .pi-logo {
      max-height: 60px;
      max-width: 180px;
      object-fit: contain;
    }

    .pi-practice-header {
      flex: 1;
    }

    .pi-practice-name {
      font-size: 16pt;
      font-weight: bold;
      margin-bottom: 8px;
      color: #000;
    }

    .pi-practice-address {
      font-size: 10pt;
      color: #555;
      line-height: 1.5;
    }

    .pi-document-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #000;
      padding-bottom: 10px;
      margin-bottom: 20px;
    }

    .pi-title {
      font-size: 18pt;
      font-weight: bold;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    .pi-date {
      font-size: 11pt;
      font-weight: normal;
    }

    .pi-body {
      margin-bottom: 30px;
    }

    .pi-section {
      margin-bottom: 25px;
      page-break-inside: avoid;
    }

    .pi-section-title {
      font-size: 13pt;
      font-weight: bold;
      margin: 0 0 12px 0;
      border-bottom: 1px solid #333;
      padding-bottom: 5px;
      text-transform: uppercase;
    }

    .pi-patient-info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px 30px;
    }

    .pi-info-item {
      display: flex;
      gap: 10px;
    }

    .pi-info-item-full {
      grid-column: 1 / -1;
    }

    .pi-info-label {
      font-weight: bold;
      min-width: 120px;
    }

    .pi-info-value {
      flex: 1;
    }

    .pi-instructions-content {
      padding: 10px;
      background-color: #f9f9f9;
      border-left: 3px solid #333;
      white-space: pre-wrap;
    }

    .pi-instructions-meta {
      display: flex;
      gap: 1rem;
      align-items: center;
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid #ddd;
      font-size: 0.875rem;
      color: #555;
    }

    .pi-instructions-badge {
      display: inline-block;
      padding: 4px 8px;
      background: #2e7d32;
      color: white;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 500;
    }

    .pi-instructions-timestamp,
    .pi-instructions-given-by {
      color: #555;
    }

    .pi-footer {
      margin-top: 40px;
      padding-top: 15px;
      border-top: 1px solid #ddd;
      font-size: 9pt;
      color: #666;
      text-align: center;
    }

    .pi-footer-note {
      margin: 5px 0;
    }

    @media print {
      .patient-instructions-print-view {
        margin: 0;
        padding: 15mm;
        box-shadow: none;
      }

      .pi-section {
        page-break-inside: avoid;
      }

      .no-print {
        display: none !important;
      }
    }

    @media screen {
      .patient-instructions-print-view {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        margin: 20px auto;
      }
    }
  `
}
